/* eslint-disable eqeqeq */
import { handleTabToNext } from '../../formKit';
import { ACCESSORY_FORM_CONFIGS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import {
  AccessoryHeading,
  AccSubtab,
  APSelect,
  ParamsEcho,
} from '../accessoryFieldKit';
import MountingSection from '../MountingSection';
import { CasingMountRow } from './LouverForm';
import AccessoryMountingPopup, {
  CasingMountSpecialParameters,
} from '../AccessoryMountingPopup';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — ENCLOSURE form (accessories/enclosure/{form.js 88,
 * form.tmpl 336}, read in full). Clone: master_enclosure_id ←
 * enclosure_params; properties (mfg/model/NEMA echo/W×H×D echo triple/door
 * config echo/door hinge/material) + mounting. The `housed_in_enclosure`
 * non-clickable toggle lives on the VFD form (the enclosure HOUSES a vfd) —
 * the enclosure form itself has no checkbox.
 * Tmpl quirk replicated: the W×H×D row opens `<label class="multiply-total
 * multiply enclosure-dimensions">` but closes `</div>` (invalid HTML —
 * browsers recover; rendered as the label element).
 */
const config = ACCESSORY_FORM_CONFIGS.enclosure;

const EnclosureForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['mounting'],
  });
  const { model } = api;
  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const params = (name: string) => api.catalogueFields.master_enclosure_id?.paramValue(name);

  return (
    <>
      <AccessoryHeading api={api} target="master_enclosure_id" visClass="vis-enclosure" />
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist">
          <AccSubtab api={api} type="enclosure" cid={cid} name="properties" sublistClass="max-height-auto">
            <APSelect api={api} target="master_enclosure_id" name="mfg_id" label="Manufacturer" required cmsPath="manufacturer" />
            {/* sync K (enclosure form.tmpl delta @28-33 / backend 0aef482bb2):
                Model no longer enumerates cms_accessory.model — the span shows
                the SAVED enclosure_params.model directly and the select keeps
                only the hidden None + a hidden current-value option (dev tmpl
                verbatim; no selected attr → value stays "None"). Model choice
                happens through the smartlist flow (AccessoryHeading
                vis-enclosure → toggleSmartList → choose:enclosure:master),
                whose /enclosures/all/ search is now paginated. */}
            <li>
              <div className="single-line">
                <span className="selection">{String(params('model') ?? '')}</span>
                <select
                  name="model"
                  className={`enclosure_params_field${api.filterFieldsDisabled ? ' non-clickable' : ''}`}
                  required
                  value="None"
                  onChange={() => undefined}
                >
                  <option value="None" className="hide">
                    - -
                  </option>
                  <option className="hide" value={String(params('model') ?? '')}>
                    {String(params('model') ?? '')}
                  </option>
                </select>
                <span className="label required">Model</span>
              </div>
            </li>
            <li>
              <div className="single-line">
                <ParamsEcho api={api} target="master_enclosure_id" field="nema_encl_type" />
                <span className="label">NEMA Type</span>
              </div>
            </li>
            {/* The `{' '}` are load-bearing — same defect as the jbox row.
                form.tmpl :47-53 puts every element on its own line, so the
                parser leaves five text nodes (unit→input→sign→input→sign→
                input) between six inline-blocks; each is 3.2px. JSX collapses
                them, so on dev3 the X separators sat tight and the third input
                finished 16px short of the `.single-line` rows around it.
                Measured against the legacy markup injected into the same live
                page: legacy inputs start at 260.7 / 355.8 / 450.8, React's at
                257.5 / 346.1 / 434.7. */}
            <li>
              <label className="multiply-total multiply  enclosure-dimensions">
                <span className="unit">in</span>{' '}
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="width"
                  className="small-input"
                  readOnly
                  value={String(params('width') ?? '')}
                  tabIndex={-1}
                  onChange={() => undefined}
                />{' '}
                <span className="multiply-sign">X</span>{' '}
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="height"
                  className="small-input"
                  readOnly
                  value={String(params('height') ?? '')}
                  tabIndex={-1}
                  onChange={() => undefined}
                />{' '}
                <span className="multiply-sign">X</span>{' '}
                <input
                  type="text"
                  data-formatter_type="decimal"
                  autoComplete="off"
                  name="depth"
                  className="small-input"
                  readOnly
                  value={String(params('depth') ?? '')}
                  tabIndex={-1}
                  onChange={() => undefined}
                />{' '}
                <span className="label">Width x Height x Depth</span>
              </label>
            </li>
            <li>
              <div className="single-line">
                <ParamsEcho api={api} target="master_enclosure_id" field="door_configuration" />
                <span className="label">Door Configuration</span>
              </div>
            </li>
            <APSelect api={api} target="master_enclosure_id" name="door_hinge_side_id" label="Door Hinge Side" required cmsPath="door_hinge_side" />
            <APSelect api={api} target="master_enclosure_id" name="mtl_id" label="Material" required cmsPath="material" />
          </AccSubtab>

          <MountingSection
            api={api}
            type="enclosure"
            cid={cid}
            variant="clone"
            sublistDisabled={model.get('master_enclosure_id') === null}
          >
            {/* inline casing_mount radio row (enclosure tmpl :312-328 —
                required label; 6e2b verifier: was missing). */}
            <CasingMountRow api={api} required />
          </MountingSection>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
      <AccessoryMountingPopup
        api={api}
        typeName="enclosure"
        typeValue="enclosure"
        SpecialParameters={CasingMountSpecialParameters}
      />
    </>
  );
};

export default EnclosureForm;
